import { useMemo, useState } from 'react';
import { HeaderBar } from '../components/HeaderBar.tsx';
import { Icon } from '../components/Icon.tsx';
import { useMe } from '../api/hooks.ts';
import { useSleep, useFeed, usePump, useDiaper } from '../api/events.ts';
import { bucketChartDays } from '../lib/chart.ts';
import type { ChartDay, FeedMark, FeedBar, DiaperMark } from '../lib/chart.ts';
import { fmtDur } from '../lib/format.ts';
import { DayTimelineSheet } from '../components/sheets/DayTimelineSheet.tsx';

export function Chart() {
  const { data: me } = useMe();
  const baby = me?.babies.find((b) => b.id === me?.active_baby_id) ?? me?.babies[0] ?? null;
  // 7-day window plus a 12h buffer on each side; bucketed to the minute so
  // the query key is stable across renders.
  const w = useMemo(() => {
    const to = Math.floor(Date.now() / 60_000) * 60 + 3600;
    const from = to - 8 * 86400;
    return { from, to, babyId: baby?.id };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baby?.id, Math.floor(Date.now() / 60_000)]);
  const sleep = useSleep(w).data ?? [];
  const feed = useFeed(w).data ?? [];
  const pump = usePump(w).data ?? [];
  const diaper = useDiaper(w).data ?? [];

  const days = useMemo(() => bucketChartDays({ days: 7, sleep, feed, pump, diaper }), [
    sleep,
    feed,
    pump,
    diaper,
  ]);

  // Rollups
  const rollups = useMemo(() => {
    const totalSleep = sleep.reduce((acc, s) => {
      const end = s.ended_at ?? Math.floor(Date.now() / 1000);
      return acc + Math.max(0, Math.floor((end - s.started_at) / 60));
    }, 0);
    const finished = sleep.filter((s) => s.ended_at);
    const avgSleep = finished.length ? Math.round(totalSleep / 7) : 0;
    const longest = finished.reduce(
      (acc, s) => Math.max(acc, Math.floor(((s.ended_at ?? 0) - s.started_at) / 60)),
      0,
    );
    return [
      { l: 'avg sleep', v: fmtDur(avgSleep) },
      { l: 'longest', v: fmtDur(longest) },
      { l: 'avg feeds', v: String(Math.round(feed.length / 7)) },
      { l: 'avg diapers', v: String(Math.round(diaper.length / 7)) },
    ];
  }, [sleep, feed, diaper]);

  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();

  const [tappedDate, setTappedDate] = useState<Date | null>(null);

  return (
    <>
      <HeaderBar title="Sleep & feed chart" subtitle="last 7 days" baby={baby} />
      <div
        className="lt-scroll"
        style={{
          display: 'flex',
          gap: 6,
          padding: '0 16px 10px',
          overflowX: 'auto',
        }}
      >
        {rollups.map((p) => (
          <div
            key={p.l}
            style={{
              flexShrink: 0,
              padding: '6px 12px',
              borderRadius: 999,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              fontSize: 12,
              color: 'var(--text-2)',
            }}
          >
            <span style={{ color: 'var(--text-3)' }}>{p.l} </span>
            <span className="lt-tabular" style={{ color: 'var(--text-1)', fontWeight: 600 }}>
              {p.v}
            </span>
          </div>
        ))}
      </div>

      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          background: 'var(--bg-app)',
          padding: '6px 16px 8px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <HourRuler />
      </div>

      <div className="lt-scroll" style={{ flex: 1 }}>
        <div style={{ padding: '8px 16px 24px' }}>
          {days.map((day, i) => (
            <ChartRow
              key={day.date.toISOString()}
              day={day}
              showNow={i === 0}
              nowMin={nowMin}
              onOpen={() => setTappedDate(day.date)}
            />
          ))}
        </div>
        <ChartLegend />
      </div>

      <DayTimelineSheet
        open={!!tappedDate}
        date={tappedDate}
        sleep={sleep}
        feed={feed}
        pump={pump}
        diaper={diaper}
        onClose={() => setTappedDate(null)}
      />
    </>
  );
}

function HourRuler() {
  const ticks = [0, 3, 6, 9, 12, 15, 18, 21, 24];
  return (
    <div style={{ paddingLeft: 42, paddingRight: 4 }}>
      <div style={{ position: 'relative', height: 14 }}>
        {ticks.map((h) => (
          <div
            key={h}
            style={{
              position: 'absolute',
              left: `${(h / 24) * 100}%`,
              transform:
                h === 0 ? 'translateX(0)' : h === 24 ? 'translateX(-100%)' : 'translateX(-50%)',
              fontFamily: 'Geist Mono, ui-monospace, monospace',
              fontSize: 10,
              color: 'var(--text-3)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {String(h).padStart(2, '0')}
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartRow({
  day,
  showNow,
  nowMin,
  onOpen,
}: {
  day: ChartDay;
  showNow: boolean;
  nowMin: number;
  onOpen: () => void;
}) {
  const [labelTop, labelSub] = day.label.split(' ');
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{ all: 'unset', display: 'flex', alignItems: 'stretch', gap: 6, marginBottom: 10, width: '100%', cursor: 'pointer' }}
    >
      <div
        style={{
          width: 36,
          paddingTop: 4,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: -0.005,
            color: showNow ? 'var(--accent)' : 'var(--text-1)',
            textAlign: 'right',
            lineHeight: 1.1,
          }}
        >
          {labelTop}
        </div>
        {labelSub && (
          <div
            className="lt-mono lt-tabular"
            style={{ fontSize: 10, color: 'var(--text-3)', textAlign: 'right' }}
          >
            {labelSub}
          </div>
        )}
      </div>

      <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {[0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875].map((p) => (
            <div
              key={p}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 6,
                left: `${p * 100}%`,
                width: 1,
                background: 'var(--divider)',
              }}
            />
          ))}
        </div>

        {/* sleep track */}
        <div
          style={{
            position: 'relative',
            height: 28,
            background: 'var(--bg-sunken)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          {day.sleeps.map((s) => {
            const left = (s.start / 1440) * 100;
            const width = ((s.end - s.start) / 1440) * 100;
            return (
              <div
                key={s.id + s.start}
                className={`${s.live ? 'lt-stripe ' : ''}${s.needsReview ? 'lt-dotted' : ''}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${left}%`,
                  width: `${width}%`,
                  background: s.kind === 'night' ? 'var(--sleep-night)' : 'var(--sleep-day)',
                  borderRadius: 4,
                }}
                title={`${s.kind} sleep`}
              />
            );
          })}
          {showNow && (
            <div
              aria-label="now"
              style={{
                position: 'absolute',
                top: -6,
                bottom: -6,
                left: `${(nowMin / 1440) * 100}%`,
                width: 1,
                background: 'var(--danger)',
                pointerEvents: 'none',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: -3,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 5,
                  height: 5,
                  borderRadius: 999,
                  background: 'var(--danger)',
                }}
              />
            </div>
          )}
        </div>

        {/* feed track: breast-duration bars under the start ticks */}
        <div style={{ position: 'relative', height: 10, marginTop: 3 }}>
          {day.feedBars.map((b, i) => (
            <FeedBarSeg key={b.id + 'b' + i} b={b} />
          ))}
          {day.feeds.map((f, i) => (
            <FeedTick key={f.id + i} f={f} />
          ))}
        </div>
        {/* diaper marks */}
        <div style={{ position: 'relative', height: 8, marginTop: 2 }}>
          {day.diapers.map((d, i) => (
            <DiaperDot key={d.id + i} d={d} />
          ))}
        </div>
      </div>
    </button>
  );
}

function FeedBarSeg({ b }: { b: FeedBar }) {
  const left = (b.start / 1440) * 100;
  // Minimum 0.4% width (~5–6px on a 400px row) so even a 1-minute segment
  // is still visible as a duration block rather than vanishing into a line.
  const widthPct = Math.max(0.4, ((b.end - b.start) / 1440) * 100);
  return (
    <div
      title={`Breast · ${b.end - b.start} min`}
      style={{
        position: 'absolute',
        left: `${left}%`,
        top: 2,
        bottom: 2,
        width: `${widthPct}%`,
        background: 'var(--breast)',
        borderRadius: 3,
      }}
    />
  );
}

function FeedTick({ f }: { f: FeedMark }) {
  const left = (f.at / 1440) * 100;
  const c =
    f.kind === 'bottle'
      ? 'var(--bottle)'
      : f.kind === 'solid'
      ? 'var(--solid)'
      : f.kind === 'pump'
      ? 'var(--pump)'
      : 'var(--breast)';
  return (
    <div
      style={{
        position: 'absolute',
        left: `${left}%`,
        top: 0,
        transform: 'translateX(-50%)',
        width: 2,
        height: 8,
        background: c,
        borderRadius: 1,
      }}
    />
  );
}

function DiaperDot({ d }: { d: DiaperMark }) {
  const left = (d.at / 1440) * 100;
  if (d.kind === 'pee' || d.kind === 'poop') {
    return (
      <div
        style={{
          position: 'absolute',
          left: `${left}%`,
          top: 0,
          transform: 'translateX(-50%)',
          width: 6,
          height: 6,
          borderRadius: 999,
          background: d.kind === 'pee' ? 'var(--pee)' : 'var(--poop)',
        }}
      />
    );
  }
  // both — split circle
  return (
    <div
      style={{
        position: 'absolute',
        left: `${left}%`,
        top: 0,
        transform: 'translateX(-50%)',
        width: 6,
        height: 6,
        borderRadius: 999,
        background: 'var(--pee)',
        overflow: 'hidden',
      }}
    >
      <div style={{ width: 3, height: 6, background: 'var(--poop)' }} />
    </div>
  );
}

function ChartLegend() {
  const items = [
    { c: 'var(--sleep-night)', l: 'Night sleep', shape: 'bar' },
    { c: 'var(--sleep-day)', l: 'Nap', shape: 'bar' },
    { c: 'var(--bottle)', l: 'Bottle', shape: 'bar' },
    { c: 'var(--breast)', l: 'Breast', shape: 'bar' },
    { c: 'var(--solid)', l: 'Solid', shape: 'bar' },
    { c: 'var(--pump)', l: 'Pump', shape: 'bar' },
    { c: 'var(--pee)', l: 'Pee', shape: 'dot' },
    { c: 'var(--poop)', l: 'Poop', shape: 'dot' },
  ];
  return (
    <div style={{ padding: '12px 16px 24px' }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
          marginBottom: 10,
        }}
      >
        Legend
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px 16px' }}>
        {items.map((it) => (
          <div
            key={it.l}
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-2)' }}
          >
            {it.shape === 'dot' ? (
              <div style={{ width: 8, height: 8, borderRadius: 999, background: it.c }} />
            ) : (
              <div style={{ width: 18, height: 10, borderRadius: 3, background: it.c }} />
            )}
            {it.l}
          </div>
        ))}
      </div>
    </div>
  );
}
